import { useCallback, useEffect, useRef } from 'react'
import * as logger from 'loglevel'
import { markClientActionPayload } from '../../Utils/actionPayloadEnvelope'
import { awaitPendingMedia } from '../../Utils/pendingMedia'
import { DEFAULT_ACTION_CONFIRM_PROMPT } from './constants'
import { getActionFormMessage } from './getActionFormMessage'
import { parseConfirmPromptAttrs } from './parseConfirmPromptAttrs'

export const closedConfirmState = {
  open: false,
  prompt: null,
  stayLabel: null,
  exitLabel: null,
  msg: null,
  timeoutEl: null,
}

export function useFrameInteractions({
  eventClickHandlers,
  anearParticipant,
  disarmTimeoutWrapper,
  commitInFlightRef,
  setActionConfirmState,
}) {
  const onNavClickRef = useRef(null)
  const onActionClickRef = useRef(null)
  const onFormSubmitRef = useRef(null)

  const publishConfirmedAction = useCallback((msg, timeoutEl) => {
    if (!anearParticipant || !msg) return
    disarmTimeoutWrapper(timeoutEl)
    anearParticipant.publishAction(msg)
  }, [anearParticipant, disarmTimeoutWrapper])

  const onNavClick = useCallback(e => {
    const navEl = e.target.closest('anear-nav')
    if (!navEl) {
      logger.warn("No anear-nav element found on click target")
      return false
    }

    const cmd = navEl.getAttribute('action')
    if (!cmd) {
      logger.warn("anear-nav element has no action attribute")
      return false
    }

    const noPrompt =
      navEl.hasAttribute('no_prompt') ||
      navEl.hasAttribute('no-prompt') ||
      navEl.getAttribute('no_prompt') === 'true' ||
      navEl.getAttribute('no-prompt') === 'true'

    const { prompt, stayLabel, exitLabel } = parseConfirmPromptAttrs(navEl)

    eventClickHandlers(e, cmd, { prompt, noPrompt, stayLabel, exitLabel, navEl })
    e.stopPropagation()
    return false
  }, [eventClickHandlers])

  const onActionClick = useCallback(e => {
    const actionEl = e.target.closest('anear-action')
    if (!actionEl) {
      logger.warn("No anear-action element found on click target")
      return false
    }

    // Timeout gating: prefer wrapper <anear-timeout>, otherwise per-action duration on the action itself.
    const timeoutEl = actionEl.closest('anear-timeout')
    if (timeoutEl && typeof timeoutEl.isActionExpired === 'function' && timeoutEl.isActionExpired()) {
      timeoutEl.requestPrompt?.()
      e.stopPropagation()
      return false
    }

    // Add visual feedback
    actionEl.classList.add('anear-action-clicked')

    const rawMsg = actionEl.getAttribute('payload')
    const msg = markClientActionPayload(rawMsg)
    if (!msg) {
      logger.warn("anear-action element has no payload attribute")
      return false
    }

    // Optional confirm (parity with <anear-nav prompt>): only when prompt attr is present.
    const { prompt, stayLabel, exitLabel } = parseConfirmPromptAttrs(actionEl)
    if (prompt) {
      setActionConfirmState({
        open: true,
        prompt: typeof prompt === 'string' ? prompt : DEFAULT_ACTION_CONFIRM_PROMPT,
        stayLabel,
        exitLabel,
        msg,
        timeoutEl,
      })
      e.stopPropagation()
      return false
    }

    // Publish action to parent event
    publishConfirmedAction(msg, timeoutEl)

    // Note: Child apps handle their own clicks independently via their own Shadow DOM handlers
    // No need to propagate actions - each app has its own action channels and state machines

    e.stopPropagation()

    return false
  }, [publishConfirmedAction, setActionConfirmState])

  const onFormSubmit = useCallback(async e => {
    e.preventDefault?.()
    // For synthetic events from external buttons, currentTarget is the form
    // For native events, target might be the form or a child, so use closest()
    const form = e.currentTarget?.tagName?.toLowerCase() === 'anear-form'
      ? e.currentTarget
      : e.target.closest('anear-form')
    if (!form) {
      logger.warn("No anear-form element found on submit target")
      return false
    }

    // Everything that must happen synchronously has to happen before the first await:
    // once we yield, propagation has already run and the SubmitEvent is stale.
    e.stopPropagation()
    const submitter = e.submitter || null

    // A capture component may still be transferring media. Awaiting it here is what makes
    // a single Share button cover both the upload and the action publish.
    if (commitInFlightRef.current.has(form)) return false
    commitInFlightRef.current.add(form)
    const timeoutEl = form.closest('anear-timeout')
    let gate
    try {
      gate = await awaitPendingMedia(form, timeoutEl)
    } finally {
      commitInFlightRef.current.delete(form)
    }

    if (!gate.proceed) {
      if (gate.expired) timeoutEl.requestPrompt?.()
      if (gate.error) {
        logger.warn(`Form submit aborted: staged media did not upload: ${gate.error.message || gate.error}`)
      }
      return false
    }

    const msg = getActionFormMessage(form, submitter)

    // Publish action to parent event
    if (anearParticipant) {
      disarmTimeoutWrapper(timeoutEl)
      anearParticipant.publishAction(msg)
    }

    // Note: Child apps handle their own clicks independently via their own Shadow DOM handlers
    // No need to propagate actions - each app has its own action channels and state machines

    return false
  }, [anearParticipant, disarmTimeoutWrapper, commitInFlightRef])

  // Keep refs pointed at the latest handlers so the safety rebind can
  // reattach click handlers without creating a dependency cycle.
  useEffect(() => {
    onNavClickRef.current = onNavClick
    onActionClickRef.current = onActionClick
    onFormSubmitRef.current = onFormSubmit
  }, [onNavClick, onActionClick, onFormSubmit])

  return { onNavClick, onActionClick, onFormSubmit, publishConfirmedAction }
}
